/**
 * 69.45, the support role's bots (core/supportRole.ts; the sheet's rule, design_sheets/specialists.html): what a bot support
 * does with its case, and how a low teammate finds it. Read by actionBot's errands (over roaming and tactics, never over a
 * fight) and its intents:
 * - a support with magazines in its case walks to the nearest low teammate within SUPPORT.walk people (the hand-out happens
 *   in reach, on its beat);
 * - with its case under a third (CASE_LOW) and none left down near it, it goes to the nearest dump — any side's within the
 *   short walk (242), else its own side's — and asks there;
 * - at a hold (its squad's hold or take of a place, or a point its side holds) with SUPPORT.dropLow teammates low within
 *   SUPPORT.dropNear people, it sets the case down; it picks it up again in reach once it has moved on (no hold here);
 * - a teammate low on rounds walks to a support with magazines nearer than its crate.
 */
import type { GameState, PlayerState, StructureState } from '../types';
import { SUPPORT } from '../constants';
import { personSetOf, type PersonSet } from '../core/scaleProfile';
import { arcDistance } from '../core/gameState';
import { supplyLow, supplyNeeds, supplyReach } from '../core/supply';
import { caseLeft, isSupport, nearestDump, refillsCase, supportWalk, dropNear, shortWalk, CASE_LOW } from '../core/supportRole';
import { squadOrderFor } from '../core/squads';
import { orderPlace } from '../core/orderForms';

const arc = (state: Pick<GameState, 'planets'>, a: Pick<PlayerState, 'planetIndex' | 'surfaceDir'>, b: Pick<PlayerState, 'planetIndex' | 'surfaceDir'>) =>
  a.planetIndex !== b.planetIndex ? Infinity : arcDistance(a.surfaceDir, b.surfaceDir, state.planets[a.planetIndex]?.radius ?? 1);

/** A teammate low on rounds (not bandages alone): what a support's case answers. */
function lowOnRounds(q: PlayerState, ps: PersonSet, state: Pick<GameState, 'config'>): boolean {
  return supplyLow(q, ps, state.config) && supplyNeeds(q, ps, state.config).rounds > 0;
}

/** The case the support left down, standing (null: none). */
export function caseOf(state: Pick<GameState, 'structures'>, unit: Pick<PlayerState, 'caseDown'>): StructureState | null {
  return unit.caseDown === undefined ? null : state.structures.find(s => s.id === unit.caseDown && s.health > 0) ?? null;
}

/** The nearest low teammate a bot support walks to (null: none, or nothing in its case). */
export function supportPatient(state: GameState, unit: PlayerState): PlayerState | null {
  if (!isSupport(state, unit) || caseLeft(unit) <= 0 || unit.mount) return null;
  const ps = personSetOf(state), reach = supportWalk(state);
  let best: PlayerState | null = null, bestD = Infinity;
  for (const q of state.players) {
    if (q.id === unit.id || q.teamIndex !== unit.teamIndex || !q.alive || q.airborne || q.mount) continue;
    const d = arc(state, unit, q);
    if (d < reach && d < bestD && lowOnRounds(q, ps, state)) { best = q; bestD = d; }
  }
  return best;
}

/** The dump a bot support goes to with its case low, and no case of its own left down near (null: none needed, or none found). */
export function supportDump(state: GameState, unit: PlayerState): StructureState | null {
  if (!isSupport(state, unit) || unit.mount || caseLeft(unit) >= CASE_LOW) return null;
  const own = caseOf(state, unit);
  if (own && arc(state, unit, own) <= dropNear(state)) return null;   // its case is down here: it stays by it
  return nearestDump(state, unit, shortWalk(state)) ?? nearestOwnDump(state, unit);
}

/** Its own side's nearest dump, any distance (the long walk is never into the enemy's base). */
function nearestOwnDump(state: GameState, unit: PlayerState): StructureState | null {
  let best: StructureState | null = null, bestD = Infinity;
  for (const s of state.structures) {
    if (s.supply?.teamIndex !== unit.teamIndex || s.supply.site !== undefined) continue;
    const d = arc(state, unit, s);
    if (d < bestD && refillsCase(s)) { best = s; bestD = d; }
  }
  return best;
}

/** A support with magazines a low teammate walks to instead of its crate, when nearer than the crate (null: none). */
export function supplierFor(state: GameState, unit: PlayerState, crateArc: number): PlayerState | null {
  if (!state.config.supportRole || unit.role === 'support') return null;
  const ps = personSetOf(state);
  if (!lowOnRounds(unit, ps, state)) return null;
  let best: PlayerState | null = null, bestD = Math.min(crateArc, supportWalk(state));
  for (const q of state.players) {
    if (q.teamIndex !== unit.teamIndex || !q.alive || q.mount || q.airborne || !isSupport(state, q) || caseLeft(q) <= 0) continue;
    const d = arc(state, unit, q);
    if (d < bestD) { best = q; bestD = d; }
  }
  return best;
}

/** At a hold: its squad's hold or take of a place within SUPPORT.dropNear people, or inside a point its side holds. */
function atHold(state: GameState, unit: PlayerState, tick: number): boolean {
  const order = squadOrderFor(state, unit.id, tick);
  if (order && (order.kind === 'hold' || order.kind === 'take')) {
    const place = orderPlace(state, order);
    if (place && arc(state, unit, place) <= dropNear(state)) return true;
  }
  const r = state.config.modeConfig.captureRadius ?? personSetOf(state).captureRadius;
  return (state.modeState.points?.sites ?? []).some(s => s.owner === unit.teamIndex && arc(state, unit, s) <= r);
}

/**
 * Does a bot support set its case down now (at a hold, SUPPORT.dropLow teammates low within SUPPORT.dropNear people, the case
 * a third full or more), or pick its own up (in reach, no longer at a hold)?
 */
export function caseDropWanted(state: GameState, unit: PlayerState, tick: number): boolean {
  if (!isSupport(state, unit) || unit.mount || unit.airborne) return false;
  const own = caseOf(state, unit);
  if (own) return arc(state, unit, own) <= supplyReach(state) && !atHold(state, unit, tick);
  if (caseLeft(unit) < CASE_LOW) return false;
  const ps = personSetOf(state), near = dropNear(state);
  let low = 0;
  for (const q of state.players) {
    if (q.id === unit.id || q.teamIndex !== unit.teamIndex || !q.alive || q.mount || arc(state, unit, q) > near) continue;
    if (lowOnRounds(q, ps, state) && ++low >= SUPPORT.dropLow) break;
  }
  return low >= SUPPORT.dropLow && atHold(state, unit, tick);
}

/** Does a bot support ask at a dump in reach: its case short of full. */
export function caseRefillAsk(state: GameState, unit: PlayerState): boolean {
  return isSupport(state, unit) && !unit.mount && caseLeft(unit) < SUPPORT.case && nearestDump(state, unit, supplyReach(state)) !== null;
}
